/**
 * Der Tarif-Katalog als Tarifkarten — rein, ohne Abruf, damit ihn auch der Server (Strukturdaten der öffentlichen Seiten) und
 * `node --test` laden. Hier steht die EINE Umrechnung der Katalogpreise
 * (`tarifkarten`); `useKatalog` gibt sie weiter, die Oberfläche importiert sie
 * von dort (`test_register_katalog_canon.py`).
 */
import type { Katalog, KatalogPlan } from '../hooks/useKatalog'
import type { JsonLd } from './structuredData'

/**
 * Eine Tarifkarte: der Katalogeintrag in der Form, die beide Preisflächen zum
 * Rendern brauchen — Preise als ZAHL in der gewählten Währung statt als Liste.
 *
 * Warum geteilt: Die Preisseite hatte diese Umrechnung inline, die
 * Registrierung las stattdessen `/api/auth/pricing` (EUR aus
 * `roles.price_monthly`). Zwei Flächen, zwei Antworten auf dieselbe Frage —
 * und die Registrierung zeigte deshalb drei statt vier Stufen und nur Euro.
 */
export interface Tarifkarte {
  code: string
  /** Einsprachig deutsch — durch `usePlanLabels().planName` schicken. */
  name: string
  /** Einsprachig deutsch — durch `usePlanLabels().planDescription` schicken. */
  description: string | null
  level: number
  scope: KatalogPlan['scope']
  /** In WÄHRUNGSEINHEITEN (nicht Cent) der gewählten Währung; `null` = kein Preis. */
  price_monthly: number | null
  price_yearly: number | null
  included_seats: number
  features: KatalogPlan['features']
}

/**
 * Die öffentlichen Stufen als Karten, aufsteigend nach `level` — der
 * Preisleiter, günstigste zuerst.
 *
 * Fehlt der Preis in der gewählten Währung, ist er `null` und NICHT umgerechnet
 * (Regel #7): Ein geratener Betrag wäre einer, der beim Kauf nicht gilt.
 */
export function tarifkarten(katalog: Katalog | undefined, waehrung: string): Tarifkarte[] {
  const betrag = (plan: KatalogPlan, interval: string): number | null => {
    const zeile = (plan.prices || []).find(
      (pr) => pr.currency === waehrung && pr.interval === interval,
    )
    return zeile ? zeile.unit_amount_cents / 100 : null
  }
  return [...(katalog?.plans || [])]
    .sort((a, b) => (a.level || 0) - (b.level || 0))
    .map((plan) => ({
      code: plan.slug,
      name: plan.name,
      description: plan.description ?? null,
      level: plan.level || 0,
      scope: plan.scope,
      price_monthly: betrag(plan, 'monthly'),
      price_yearly: betrag(plan, 'yearly'),
      included_seats: plan.included_seats ?? 1,
      features: plan.features || {},
    }))
}

/**
 * Das `AggregateOffer` des Produkts (SEO-Paket D1): die Tarife in der Währung
 * der Länderversion (`waehrung` = `currencyForCountryCode(land)`,
 * dieselbe Vorauswahl wie die Preisseite), Monatspreise. `lowPrice` ist 0 —
 * den kostenlosen Tarif gibt es in jedem Markt. Kein Preis steht im Code; fehlt
 * die Währung im Katalog, gibt es kein Angebot, und das Log sagt es (Regel #7).
 */
export function aggregateOffer(katalog: Katalog, land: string, waehrung: string): JsonLd | null {
  const karten = tarifkarten(katalog, waehrung)
  const preise = karten.flatMap((k) => (k.price_monthly === null ? [] : [k.price_monthly]))
  if (preise.length === 0) {
    console.error(`aggregateOffer: kein Monatspreis in ${waehrung} für ${land} — Angebot weggelassen`)
    return null
  }
  return {
    '@type': 'AggregateOffer',
    priceCurrency: waehrung,
    lowPrice: 0,
    highPrice: Math.max(...preise),
    offerCount: karten.length,
  }
}
